import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { Box } from "@mui/material";
import CreatorIcon from "../VectorIcons/CreatorIcon";
import TypeIconReturner from "../TypeIconReturner/TypeIconReturner";
import SheetIcon from "../VectorIcons/SheetIcon";
import AttachmentsList from "../ListAttachments/ListAttachments";
import NoResultsFiles from "../NoResultsFiles/NoResultsFiles";
import ContentActivityFooter from "../../views/Activities/NewActivity/ContentActivityFooter/ContentActivityFooter";
import AppContext from "../../context/appContext";
import { countSelectedAttachments } from "../../utils";

const RESEARCH_TYPE = "Pesquisa";

// Renders an inline list of labels separated by a pipe ("|"),
// reused for creators, researchers and origins.
const SeparatedList = ({ items }) => (
  <>
    {items.map((item, index) => (
      <Box key={`${item}-${index}`}>
        <Box component="span">{item}</Box>
        {index + 1 < items.length && (
          <Box component="span" className="creatorSeparator">
            |
          </Box>
        )}
      </Box>
    ))}
  </>
);

export const ContentAttachmentsHeader = ({
  detailsHeader = "Detalhes",
  detailsProps,
  lastAttachmentHeader = "Último anexo",
  lastAttachmentProps,
  disabled,
}) => (
  <Box className={`header${disabled ? " disabled" : ""}`}>
    <Box component="li" className="title">
      Título
    </Box>

    <Box component="li" className="creator">
      <CreatorIcon color="#575863" />
    </Box>

    <Box component="li" className="details" {...detailsProps}>
      {detailsHeader}
    </Box>
    <Box component="li" className="origin">
      Procedência
    </Box>
    <Box component="li" className="lastAttachment" {...lastAttachmentProps}>
      {lastAttachmentHeader}
    </Box>
  </Box>
);

export const ContentAttachmentsResults = ({
  results = [],
  renderResult,
  detailsHeader,
  detailsProps,
  lastAttachmentHeader,
  lastAttachmentProps,
  disabledHeader,
  showHeader = true,
  children,
}) => (
  <Box className="contentItem">
    {showHeader && (
      <ContentAttachmentsHeader
        detailsHeader={detailsHeader}
        detailsProps={detailsProps}
        lastAttachmentHeader={lastAttachmentHeader}
        lastAttachmentProps={lastAttachmentProps}
        disabled={disabledHeader}
      />
    )}

    {results.map(renderResult)}
    {children}
  </Box>
);

const ContentAttachmentsList = (props) => {
  const {
    data,
    files,
    results,
    toggleActiveCheckBox,
    toggleActiveVersionCheckBox,
    toggleOpenList,
    showFooter,
    createIsValid,
    onCancel,
  } = props;

  const result = results?.[0] ?? { data, files };
  const resultData = result.data;
  const resultFiles = result.files ?? [];
  const appContext = useContext(AppContext);

  if (!resultData) return null;

  const {
    type,
    title,
    creators,
    researchers,
    format,
    soapOperaSchedule,
    researchCategory,
    researchSubCategory,
    lastAttachmentReceivedDate,
    id,
    origins,
  } = resultData;

  const isResearch = type === RESEARCH_TYPE;
  const hasFiles = resultFiles.length > 0;

  const getRedirectPath = () => appContext.redirectToSheet(type, id);

  const peopleNames = isResearch ? researchers : creators.map((c) => c.name);

  const detailsText = isResearch
    ? `${researchCategory} • ${researchSubCategory}`
    : [format, format === "Novela" ? soapOperaSchedule : null]
        .filter(Boolean)
        .join(" • ");

  const lastAttachmentText = lastAttachmentReceivedDate
    ? appContext.formatDate(
        appContext.convertDatePtBr(lastAttachmentReceivedDate)
      )
    : "-";

  const handleCreateActivity = () =>
    appContext.setCurrentStepBox(
      isResearch ? "createResearchActivity" : "createProjectActivity"
    );

  return (
    <ContentAttachmentsResults
      results={[result]}
      renderResult={() => (
      <Box className="listItem opened">
        <Box className="projectDetails" onClick={toggleOpenList}>
          <Box component="li" className="title">
            <Box className="itemArrow" />

            <TypeIconReturner type={type} responsiveIcon />

            <Box component="span" className="projectTitle">
              {title}
            </Box>
          </Box>

          <Box component="li" className="creator">
            <SeparatedList items={peopleNames} />
          </Box>

          <Box component="li" className="details">
            <Box component="span">{detailsText}</Box>
          </Box>

          <Box component="li" className="origin">
            {!!origins && <SeparatedList items={origins} />}
          </Box>

          <Box component="li" className="lastAttachment">
            <Box component="span">{lastAttachmentText}</Box>
          </Box>

          <Link to={getRedirectPath()}>
            <SheetIcon />
          </Link>
        </Box>

        <Box className="projectAttachments">
          {hasFiles ? (
            <>
              <AttachmentsList
                files={resultFiles}
                toggleActiveCheckBox={toggleActiveCheckBox}
                toggleActiveVersionCheckBox={toggleActiveVersionCheckBox}
                contentType={type}
                contentFormat={format}
              />

              {showFooter && (
                <ContentActivityFooter
                  isValid={createIsValid()}
                  selectedCount={countSelectedAttachments(resultFiles)}
                  onCreateActivity={handleCreateActivity}
                  onCancel={onCancel}
                />
              )}
            </>
          ) : (
            <NoResultsFiles redirectToSheet={getRedirectPath} />
          )}
        </Box>
      </Box>
      )}
    />
  );
};

export default React.memo(ContentAttachmentsList);